import {
  Column,
  Columns,
  FlexBox,
  Icon,
  List,
  Text,
} from '@dixa/lib-shared-habla';
import classNames from 'classnames';
import {
  AIInsightInstrumentPrompt,
  ReadInstrumentsQueryFn,
} from 'lib-analytics-types';
import { ampli } from 'lib-shared-tracking';
import { memo, useCallback, useState } from 'react';

import { useAnalyticsState } from '../../../../../../utils/useAnalyticsState/useAnalyticsState';

import styles from './aiInsightInstrumentRenderer.module.scss';
import {
  AIInsightInstrument,
  AIInsightInstrumentProps,
} from './components/instrument/aiInsightInstrument';

type AIInsightInstrumentRendererProps = {
  /** The list of prompts available to generate AI insights. */
  prompts: readonly AIInsightInstrumentPrompt[];
  /** Called to fetch the instrument data. */
  onReadInstruments: ReadInstrumentsQueryFn;
} & Pick<AIInsightInstrumentProps, 'panel' | 'instrument' | 'requestId'>;

export const AIInsightInstrumentRenderer = memo(
  function AIInsightInstrumentRenderer({
    panel,
    instrument,
    prompts,
    requestId,
    onReadInstruments,
  }: AIInsightInstrumentRendererProps): JSX.Element {
    const { dashboard } = useAnalyticsState(panel);

    const [selectedPrompt, setSelectedPrompt] =
      useState<AIInsightInstrumentPrompt | null>(null);

    const handleSelectPrompt = useCallback(
      (prompt: AIInsightInstrumentPrompt) => {
        setSelectedPrompt(prompt);

        onReadInstruments({
          meta: {
            ai_prompt_id: prompt.id,
          },
        });

        ampli.analyticsInsightsPromptClicked({
          dashboard: dashboard.title,
          panel: panel.title,
          prompt: prompt.title,
        });
      },
      [dashboard.title, onReadInstruments, panel.title],
    );

    const handleRegenerate = useCallback(() => {
      if (!selectedPrompt) return;

      onReadInstruments({
        meta: {
          ai_prompt_id: selectedPrompt.id,
        },
      });
    }, [onReadInstruments, selectedPrompt]);

    return (
      <Columns className={styles.columns}>
        <Column>
          <Column.Row
            scrollable
            noMinHeight
            noVerticalPadding
            noHorizontalPadding
          >
            <FlexBox flexDirection="column" gap="400">
              <Text.Paragraph variant="2" subdued>
                Leveraging AI technology, this feature dives deep into customer
                ratings and comments, equipping you with invaluable insights to
                refine practices, elevate customer satisfaction, and pinpoint
                opportunities for growth and exceptional service delivery.
              </Text.Paragraph>

              <Text.Paragraph variant="1" subdued>
                Choose an insight
              </Text.Paragraph>

              <List className={styles.list}>
                {prompts.map((prompt) => (
                  <List.Item
                    key={prompt.id}
                    className={classNames(styles.listItem, {
                      [styles.listItemSelected]:
                        prompt.id === selectedPrompt?.id,
                    })}
                    variant="large"
                    title={prompt.title}
                    titleSecondary={prompt.description}
                    contentLeft={
                      <Icon
                        name={prompt.icon ?? 'ai'}
                        color="purple.700"
                        size={24}
                        className={styles.icon}
                      />
                    }
                    withHoverEffect={false}
                    onClick={() => handleSelectPrompt(prompt)}
                  />
                ))}
              </List>
            </FlexBox>
          </Column.Row>
        </Column>

        <Column>
          <Column.Row
            className={styles.rightColumn}
            scrollable
            noMinHeight
            noVerticalPadding
            noHorizontalPadding
          >
            <AIInsightInstrument
              panel={panel}
              instrument={instrument}
              prompt={selectedPrompt}
              requestId={requestId}
              onRegenerate={handleRegenerate}
            />
          </Column.Row>
        </Column>
      </Columns>
    );
  },
);
